import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";
import axios from "axios";
import trackEvent from "../app_proxy/trackEvent";
import { appsignal } from "../services/appsignalService";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import { Liquid } from "liquidjs";

const engine = new Liquid({preserveTimezones: true})
const wrapperTemplate = `<div class="listing" data-action="click->map#setActiveLocation" data-map-id-param="{{ location.id }}" data-map-target="listing" data-location-id="{{ location.id }}" data-location-slug="{{ location.slug }}" data-lat='{{location.lat }}' data-lng='{{ location.lng }}'></div>`;

export default class extends Controller {
  static targets = [
    "map",
    "search",
    "listingsContainer",
    "listing",
    "listingTemplate",
    "markerTemplate",
    "popupTemplate",
    "searchMarker",
    "listingsLoadingContent",
    "listingsEmptyContent",
    "searchResult",
    "searchButton",
  ];
  static values = {
    locations: Array, // Not using, yet
    activeLocationId: Number,
    activeTags: Array,
    searchAsIMoveTheMap: { type: Boolean, default: false },
    geolocateOnLoad: { type: Boolean, default: false },
    keepSearchInView: { type: Boolean, default: true },
    openNow: { type: Boolean, default: false },
    selectFirstListingOnResults: { type: Boolean, default: false },
    boundsPadding: {
      type: Object,
      default: { left: 100, right: 100, top: 100, bottom: 100 },
    },
    maxResults: { type: Number, default: 30 },
    maxDistance: { type: Number, default: 50 },
    maxDistanceUnits: { type: String, default: "mi" },
    animationDuration: { type: Number, default: 1.2 },
    mapboxOptions: { type: Object, default: {} },
    geocoderOptions: { type: Object, default: {} },
    filterBehaviour: { type: String, default: "all" },
    moveCamera: { type: Boolean, default: false },
  };

  connect() {
    this.element[this.identifier] = this;

    this.noResultsClass = "no-results";
    this.resultsLoadingClass = "results-loading";
    this.geolocateLoadingClass = "geolocate-loading";
    this.searchFocusedClass = "search-focused";
    this.hasActiveTagsClass = "has-active-tags";
    this.hasOpenNowFilterClass = "has-open-now-filter";
    this.searchAsIMoveTheMapClass = "search-on-map-move";

    this.abortController = new AbortController();

    // Get content from templates
    this.listingTemplate = this.listingTemplateTarget.innerHTML;
    if (this.hasMarkerTemplateTarget)
      this.markerTemplate = this.markerTemplateTarget.innerHTML;
    if (this.hasPopupTemplateTarget)
      this.popupTemplate = this.popupTemplateTarget.innerHTML;
    if (this.hasSearchMarkerTarget)
      this.searchMarkerTemplate = this.searchMarkerTarget.innerHTML;

    // Get state content
    this.listingsLoadingContent = this.listingsLoadingContentTarget.innerHTML;
    this.listingsEmptyContent = this.listingsEmptyContentTarget.innerHTML;

    // build elements e.g. map, geocode etc..
    this.setListingsLoading();
    this.buildElements();
  }

  random4Numbers() {
    return (Math.floor(Math.random() * 10000) + 10000).toString().substring(1);
  }

  listingsContainerTargetConnected(listingsContainerElement) {
    // Create a unique ID
    listingsContainerElement.id = `listings-${this.random4Numbers()}`;
  }

  // This gets triggered when we get a search result back
  searchResultTargetConnected(searchResultElement) {
    this.element.classList.remove(this.noResultsClass);
    this.element.classList.remove(this.resultsLoadingClass);

    this.clearPopUp();

    if (!this.listingTargets.length) {
      this.setListingsEmpty();
      return;
    }

    const { moveCamera, setFirstActive } = searchResultElement.dataset;

    if (setFirstActive === "true") {
      // // Stop any current movement
      // this.map.stop();
      // Fit the bounds
      this.map.fitBounds(this.listingBounds(), {
        padding: this.boundsPaddingValue,
      });

      const firstListingId = this.listingTargets[0].dataset.locationId;
      this.activeLocationIdValue = firstListingId;

      return;
    }

    if (moveCamera === "false") return;

    if (this.searchCoords && this.selectFirstListingOnResultsValue) {
      // If we have a search, set the first listing to be active
      const firstListingId = this.listingTargets[0].dataset.locationId;
      this.activeLocationIdValue = firstListingId;
    } else {
      if (this.searchAsIMoveTheMapValue) return;
      // If we dont have a search, fit them all in.
      this.map.fitBounds(this.listingBounds(), {
        padding: this.boundsPaddingValue,
      });
    }
  }

  clearActiveLocation(e) {
    e.stopPropagation();
    this.activeLocationIdValue = undefined;
  }

  clearPopUp() {
    if (this.popup) this.popup.remove();
  }

  setListingsLoading() {
    this.element.classList.add(this.resultsLoadingClass);
    // Some page variants have no listings panel; skip instead of throwing.
    if (!this.hasListingsContainerTarget) return;
    this.listingsContainerTarget.innerHTML = this.listingsLoadingContent;
  }

  setListingsEmpty() {
    this.element.classList.add(this.noResultsClass);
    if (!this.hasListingsContainerTarget) return;
    this.listingsContainerTarget.innerHTML = this.listingsEmptyContent;
  }

  buildElements() {
    if (this.hasMapTarget) this.buildMap();
    if (this.hasSearchTarget) this.buildGeocoder();
    this.buildGeolocator();
  }

  buildMap() {
    window.Wrapped = undefined; //Fix for this app being weird https://apps.shopify.com/wrapped
    if (!window.locationsMadeSuperConfig.mapboxKey) {
      this.handleMapError("Mapbox key not found");
      return;
    }

    mapboxgl.accessToken = window.locationsMadeSuperConfig.mapboxKey;

    let mapOptions = {
      maxZoom: 17,
      scrollZoom: true,
      style: window.locationsMadeSuperConfig.mapStyle,
      bounds: window.locationsMadeSuperConfig.boundingBox,
      ...this.mapboxOptionsValue,
      container: this.mapTarget,
      logoPosition: window.locationsMadeSuperConfig.hideWhitelabel
        ? "bottom-left"
        : "top-left",
    };

    this.map = new mapboxgl.Map(mapOptions);

    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }));

    // Set bounds again with padding because we cant in bounds:
    if (window.locationsMadeSuperConfig.boundingBox) {
      this.map.fitBounds(window.locationsMadeSuperConfig.boundingBox, {
        padding: this.boundsPaddingValue,
      });
    }

    // Event listeners
    this.map.on("error", (e) => {
      const detail = e?.error?.message || "";
      // Token rejected for this URL (403) or invalid (401): say so instead
      // of a generic message, so merchants know it's a token/domain issue.
      if (/forbidden|unauthorized|\b40[13]\b/i.test(detail)) {
        this.handleMapError(
          "The map could not load because the Mapbox token is not valid for this domain.",
          e
        );
      } else {
        this.handleMapError("Error loading map", e);
      }
    });
    this.map.on("load", this.handleMapLoad.bind(this));
    this.map.on("dragend", this.handleMapMove.bind(this));
    this.map.on("zoomend", this.handleMapMove.bind(this));
  }

  buildGeocoder() {
    let geocoderOptions = this.geocoderOptionsValue;
    geocoderOptions.accessToken = mapboxgl.accessToken;
    geocoderOptions.mapboxgl = mapboxgl;
    geocoderOptions.flyTo = false;
    geocoderOptions.marker ??= !this.hasSearchMarkerTarget;
    geocoderOptions.placeholder ??= "Enter address or zipcode..";

    const proxyPath = this.getLocaleAwareProxyPath();
    geocoderOptions.externalGeocoder = async (query) => {
      const response = await axios.post(
        `${proxyPath}/location_search_results`,
        { search_text: query }
      );
      return response.data;
    };

    this.geocoder = new MapboxGeocoder(geocoderOptions);

    // Add to searchTarget div
    this.searchTarget.appendChild(this.geocoder.onAdd(this.map));

    // Update proximity if it was set in options
    if (geocoderOptions.proximity) {
      this.geocoder.setProximity(geocoderOptions.proximity);
    }

    // this.geocoder._inputEl.addEventListener("click", () =>
    //   this.element.classList.add(this.searchFocusedClass)
    // );

    // this.searchTarget
    //   .querySelector("input.mapboxgl-ctrl-geocoder--input")
    //   .setAttribute("type", "search");
    // Event listeners
    this.geocoder.on("clear", this.clearSearchCoords.bind(this));
    this.geocoder.on("result", this.handleGeocoderSearch.bind(this));
  }

  unfocusSearch() {
    this.element.classList.remove(this.searchFocusedClass);
  }

  handleGeocoderSearch(search) {
    const locationResult = search.result.type === "LocationResult";

    this.clearSearchMarker();

    this.element.classList.remove(this.searchFocusedClass);
    this.geocoder._inputEl.blur();

    if (this.hasSearchMarkerTarget) {
      // Add custom search marker to map
      let newMarker = document.createElement("div");
      newMarker.innerHTML = this.searchMarkerTemplate;

      const searchMarker = new mapboxgl.Marker(newMarker, {
        anchor: "bottom",
      });
      searchMarker.setLngLat(search.result.center).addTo(this.map);
      if (searchMarker._element)
        searchMarker._element.classList.add("search-marker");
    }

    let properties = {};

    const place_type = search.result.place_type[0];
    properties[place_type] = search.result.text;
    if (search.result.context) {
      search.result.context.forEach((contextItem) => {
        const context_place_type = contextItem.id.split(".")[0];
        properties[context_place_type] = contextItem.text;
      });
    }
    properties.center = search.result.center;
    properties.text = search.result.text;
    trackEvent("Map search", properties);

    // Mapbox returns center as [lng, lat]. Build a new [lat, lng] array instead
    // of using Array#reverse(), which mutates the geocoder's cached result in
    // place and corrupts the coords on every subsequent re-selection.
    const [lng, lat] = search.result.center;
    this.searchCoords = [lat, lng];
    // console.log(this.searchCoords);
    this.search({ locationResult: locationResult });
  }

  // Resets the search marker + coords without triggering a query. Used by the
  // result handler, which fires its own (correctly geocoded) search right after.
  clearSearchMarker() {
    document
      .querySelectorAll(".mapboxgl-marker.search-marker")
      .forEach((marker) => {
        // console.log(marker);
        if (marker) marker.remove();
      });
    this.searchCoords = undefined;
  }

  // Bound to the geocoder "clear" (X) event: reset state and re-show all locations.
  clearSearchCoords() {
    this.clearSearchMarker();
    this.search();
  }

  buildGeolocator() {
    this.geolocator = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: Infinity,
      },
    });
    // hacky workaround for the fact that mapbox doesn't let you disable camera auto-tracking
    this.geolocator._updateCamera = () => {};

    // Add to map
    this.map.addControl(this.geolocator);
    // Event listeners
    this.geolocator.on("geolocate", this.handleGeolocate.bind(this));
    this.geolocator.on("error", this.handleGeolocateError.bind(this));
  }

  handleGeolocateError(error) {
    this.search();
  }

  handleGeolocate(result) {
    this.searchCoords = [result.coords.latitude, result.coords.longitude];
    
    // Test if we can change the geocoderOptionsValue

    this.geocoderOptionsValue = {
      ...this.geocoderOptionsValue,
      proximity: `${this.searchCoords[1]},${this.searchCoords[0]}`
    };
    

    
    // Remove existing geocoder element from DOM to prevent duplicates
    const existingGeocoder = this.searchTarget.querySelector('.mapboxgl-ctrl-geocoder');
    if (existingGeocoder) {
      existingGeocoder.remove();
    }
    
    // Set geocoder to null to force recreation (avoids remove() method issue)
    this.geocoder = null;
    
    // Rebuild geocoder with updated options
    this.buildGeocoder();

    this.search();
  }

  handleMapError(message, error = undefined) {
    if (error) {
      appsignal.sendError(error.error, (span) => {
        span.setAction("map controller");
        span.setNamespace("app_proxy");
      });
      console.error(error);
    }
    this.element.innerHTML = `<div style='padding: 20px;'><p>${message}</p></div>`;
  }

  handleMapLoad() {
    if (!window.locationsMadeSuperConfig.hideWhitelabel) {
      const svg =
        '<svg style="height: 28px; margin-bottom: -6px;" data-name="Group 440" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1737.635 432"><defs><clipPath id="a"><path data-name="Rectangle 2710" fill="none" d="M0 0h1737.635v432H0z"/></clipPath><clipPath id="b"><path data-name="Rectangle 2709" fill="none" d="M0 0h1737.635v432.004H0z"/></clipPath><clipPath id="c"><path data-name="Rectangle 2697" fill="none" d="M0 0h174.246v207.718H0z"/></clipPath><clipPath id="d"><path data-name="Rectangle 2698" fill="none" d="M0 0h164.509v202.342H0z"/></clipPath><clipPath id="e"><path data-name="Rectangle 2699" fill="none" d="M0 0h154.061v197.55H0z"/></clipPath><clipPath id="f"><path data-name="Rectangle 2700" fill="none" d="M0 0h138.159v201.09H0z"/></clipPath><clipPath id="g"><path data-name="Rectangle 2701" fill="none" d="M0 0h145.123v200.013H0z"/></clipPath><clipPath id="h"><path data-name="Rectangle 2702" fill="none" d="M0 0h153.823v202.607H0z"/></clipPath><clipPath id="i"><path data-name="Rectangle 2703" fill="none" d="M0 0h148.367v199.513H0z"/></clipPath><clipPath id="j"><path data-name="Rectangle 2704" fill="none" d="M0 0h138.159v201.092H0z"/></clipPath><clipPath id="k"><path data-name="Rectangle 2705" fill="none" d="M0 0h152.176v200.006H0z"/></clipPath><clipPath id="l"><path data-name="Rectangle 2706" fill="none" d="M0 0h710.121v128.685H0z"/></clipPath><clipPath id="m"><path data-name="Rectangle 2707" fill="none" d="M0 0h106.761v131.348H0z"/></clipPath><clipPath id="n"><path data-name="Rectangle 2708" fill="none" d="M0 0h106.762v133.39H0z"/></clipPath></defs><g data-name="Group 439" clip-path="url(#a)"><g data-name="Group 438"><g data-name="Group 437" clip-path="url(#b)" transform="translate(0 -.004)"><path data-name="Path 488" d="M204.421 57.826c-109.066 0-197.489 82.677-197.489 184.666a174.908 174.908 0 0 0 27.858 94.621c-16.891 7.78-12.541 30.448-.783 42.255a34.826 34.826 0 0 0 43.425 4.56c34.33 26.986 78.634 43.239 126.99 43.239 109.066 0 197.489-82.677 197.489-184.674S313.488 57.826 204.421 57.826" fill="#f7f7ea"/><path data-name="Path 489" d="M397.295 241.83c.008 5.7 8.869 5.714 8.861 0 .008-5.7-8.853-5.705-8.861 0" fill="#666"/><path data-name="Path 490" d="M23.129 310.249a172.267 172.267 0 0 1-13.695-54.2 170.477 170.477 0 0 1 4.882-57.079 172.849 172.849 0 0 1 22.628-51.115 186.007 186.007 0 0 1 37.195-41.649 197.954 197.954 0 0 1 48.832-29.649 204.571 204.571 0 0 1 57.257-14.833 210.186 210.186 0 0 1 61.7 2.4 202.072 202.072 0 0 1 55.489 19.174 193.218 193.218 0 0 1 46.184 33.367 181.286 181.286 0 0 1 33.45 44.474 169.844 169.844 0 0 1 20.24 80.692c.016-5.714 8.869-5.705 8.861 0a179.955 179.955 0 0 0-9.837-58.621 184.634 184.634 0 0 0-27.454-51.2 200.212 200.212 0 0 0-42.66-41.412 206.808 206.808 0 0 0-53.52-27.478 214.485 214.485 0 0 0-61.816-11.33 216.971 216.971 0 0 0-63.277 7.045 208.946 208.946 0 0 0-55.8 23.879 200.254 200.254 0 0 0-45.127 37.824 187.428 187.428 0 0 0-31.489 49.111A181.611 181.611 0 0 0 .601 227.337a179.515 179.515 0 0 0 5.173 59.7 182.645 182.645 0 0 0 23.484 53.181 55.935 55.935 0 0 1 8.619-3.01 180.2 180.2 0 0 1-14.748-26.959" fill="#666"/><path data-name="Path 491" d="M397.294 241.83a170.529 170.529 0 0 1-9.523 56.264 177.883 177.883 0 0 1-26.404 48.87 189.5 189.5 0 0 1-39.85 38.6 199.071 199.071 0 0 1-51.446 26.518 205.893 205.893 0 0 1-59.492 10.943 207.43 207.43 0 0 1-60.638-6.747 198.947 198.947 0 0 1-53.448-22.845 193.346 193.346 0 0 1-19.336-13.55l-7.734 5.101a205.08 205.08 0 0 0 49.606 29.988 215.08 215.08 0 0 0 60.924 15.792 217.846 217.846 0 0 0 64.06-2.631 210.49 210.49 0 0 0 57.886-20.127 202.612 202.612 0 0 0 47.968-34.749 191.15 191.15 0 0 0 34.956-46.498 180.118 180.118 0 0 0 18.86-55.336 184.5 184.5 0 0 0 2.469-29.593c.008 5.714-8.853 5.7-8.861 0" fill="#666"/><path data-name="Path 492" d="m288.277 159.669-90.54 46.233s32.974 58.7 80.781 42.053 9.757-88.286 9.757-88.286m.46 76.092c-18.642 18.73-48.21 4.285-48.21 4.285a26.221 26.221 0 0 1 9.934-14.478c8.118-5.827 22.047 2.332 25.534 0s-6.964-5.246-6.964-5.246c22.628-12.242 27.712 3.4 27.712 3.4a45.292 45.292 0 0 1-8.005 12.04" fill="#666"/><path data-name="Path 493" d="M143.362 233.559a5.354 5.354 0 0 1-2.76-.759 5.418 5.418 0 0 1-1.886-7.417l21.224-35.677-33.951.541a5.412 5.412 0 1 1-.169-10.822l36.428-.581h.214c3.809 0 6.924 1.606 8.562 4.439 1.67 2.881 1.477 6.464-.516 9.821l-22.488 37.808a5.4 5.4 0 0 1-4.658 2.647" fill="#666"/><path data-name="Path 494" d="M276.89 141.608c-3.793 0-6.892-1.6-8.53-4.406-1.679-2.873-1.509-6.456.484-9.821l15.139-25.679a5.412 5.412 0 0 1 9.321 5.5l-13.88 23.536 33.426-.654.113-.008a5.411231 5.411231 0 0 1 .1 10.822l-35.912.71h-.266" fill="#666"/><path data-name="Path 495" d="M37.832 330.68c-7.771 1.485-15.341 5.94-18.674 13.4a27.18 27.18 0 0 0-2.381 11 34.942 34.942 0 0 0 1.679 10.705 40.655 40.655 0 0 0 12.436 19 40.111 40.111 0 0 0 25.7 9.329 39.641 39.641 0 0 0 26.57-10.535q1.319-1.223 2.518-2.55a4.428 4.428 0 0 0-6.262-6.262 38.88 38.88 0 0 1-4.89 4.543c.3-.234.6-.46.9-.694a35.441 35.441 0 0 1-7.739 4.535c.355-.153.71-.3 1.057-.452a35.188 35.188 0 0 1-8.772 2.4c.4-.056.783-.1 1.178-.161a35.664 35.664 0 0 1-9.41 0c.4.048.783.1 1.178.161a36.337 36.337 0 0 1-9.006-2.453c.355.145.7.3 1.057.444a35.284 35.284 0 0 1-7.788-4.527c.3.234.6.46.9.694a34.829 34.829 0 0 1-6.109-6.125c.226.3.46.6.694.9a37.889 37.889 0 0 1-4.939-8.474c.153.355.3.71.452 1.065a35.064 35.064 0 0 1-2.437-8.78l.161 1.17a26.908 26.908 0 0 1-.056-7.021c-.048.387-.1.783-.153 1.17a20.843 20.843 0 0 1 1.428-5.342c-.153.355-.3.71-.452 1.057a18.574 18.574 0 0 1 2.348-4.019c-.234.3-.46.6-.694.9a19.129 19.129 0 0 1 3.268-3.244c-.3.234-.6.468-.9.694a22.021 22.021 0 0 1 4.769-2.736l-1.065.449a28.846 28.846 0 0 1 5.786-1.695 4.436 4.436 0 0 0 1.057-8.1 4.949 4.949 0 0 0-3.414-.444" fill="#666"/><path data-name="Path 496" d="M39.196 347.903c-4.366 4.486-4.035 11.306-1.151 16.495a18.671 18.671 0 0 0 14.889 9.628 12 12 0 0 0 8.971-3.345 4.42896402 4.42896402 0 1 0-6.26-6.267c-.226.218-.46.42-.7.613.3-.234.6-.46.9-.694a7.354 7.354 0 0 1-1.469.839c.355-.145.71-.291 1.057-.444a8.6 8.6 0 0 1-1.961.533c.4-.056.791-.1 1.178-.161a10.113 10.113 0 0 1-2.469 0c.4.048.783.1 1.178.161a12.394 12.394 0 0 1-3.083-.847c.355.153.7.3 1.057.452a14.954 14.954 0 0 1-3.163-1.848c.3.226.6.46.9.686a16.224 16.224 0 0 1-2.881-2.881c.234.3.46.6.694.9a14.976 14.976 0 0 1-1.832-3.147c.145.355.3.71.444 1.065a13.109 13.109 0 0 1-.847-3.067l.161 1.178a10.128 10.128 0 0 1-.008-2.615c-.056.387-.1.783-.161 1.17a8.4 8.4 0 0 1 .533-1.937c-.145.347-.3.7-.444 1.057a7.02 7.02 0 0 1 .928-1.574c-.234.3-.468.6-.694.9q.242-.3.508-.581a4.428 4.428 0 1 0-6.262-6.262" fill="#666"/><path data-name="Path 497" d="M153.074 102.09c-.331.258-.3.234-.178.137l.04-.04c.145-.113.767-.589.137-.1" fill="#666"/><path data-name="Path 498" d="m115.127 140.648.113-.137c-.694.791-.371.428-.113.137" fill="#666"/><path data-name="Path 499" d="M187.926 81.568c-.4.169-.621.274 0 0" fill="#666"/><path data-name="Path 500" d="m68.974 222.373.073-.161c.242-.581-.016.024-.073.161" fill="#666"/><path data-name="Path 501" d="M302.932 75.305c-.436-.226-.88-.436-1.323-.646-1.59-.751-3.2-1.436-4.826-2.106-2-.831-4.027-1.6-6.069-2.316q-4.043-1.416-8.175-2.582-4.975-1.416-10.055-2.461c-4.237-.888-8.506-1.558-12.8-2.1q-6.536-.837-13.129-1.072a143.912 143.912 0 0 0-14.453.21 151.961 151.961 0 0 0-16.544 2 140.549 140.549 0 0 0-16.358 3.906 157.693 157.693 0 0 0-35.266 15.793 180.549 180.549 0 0 0-18.408 12.9 197.771 197.771 0 0 0-17.707 15.879 235.458 235.458 0 0 0-17.536 19.86 302.072 302.072 0 0 0-17.609 24.751c-5.98 9.361-11.476 19.037-16.568 28.907-5.859 11.346-11.161 22.975-16.084 34.758-5.423 12.977-10.322 26.171-14.792 39.5q-7.491 22.371-13.523 45.186-3.22 12.117-6.109 24.307a4.515 4.515 0 0 0 3.1 5.455 4.5 4.5 0 0 0 5.447-3.1c3.817-16.108 8.046-32.111 12.807-47.968 4.261-14.2 8.942-28.285 14.155-42.174q3.559-9.49 7.457-18.852c.145-.339.291-.678.428-1.017l.048-.121-.065.161c.056-.137.307-.742.065-.161l.145-.339c.331-.783.662-1.566 1-2.34.638-1.493 1.283-2.97 1.937-4.455q1.888-4.261 3.849-8.49c4.947-10.62 10.3-21.063 16.14-31.223 5.165-8.99 10.725-17.762 16.737-26.211q2.106-2.978 4.3-5.9c.654-.863 1.275-1.767 1.969-2.6-.242.3-.565.662.113-.137l-.113.137.145-.178c.161-.2.323-.412.484-.613.371-.476.751-.952 1.122-1.428q4.188-5.278 8.651-10.322a216.406 216.406 0 0 1 17.568-17.7q4.285-3.825 8.78-7.384l1.025-.807c-.129.1-.161.121.169-.129.638-.492.024-.016-.121.1l-.048.032c.137-.1.379-.291.46-.347.734-.565 1.477-1.122 2.219-1.67q2.143-1.586 4.342-3.107a161.7 161.7 0 0 1 17.5-10.572q4.237-2.2 8.595-4.132c.718-.315 1.436-.613 2.155-.928l-.234.1c.113-.048.242-.1.371-.153.339-.145.686-.282 1.033-.428q2.118-.847 4.261-1.638a140.769 140.769 0 0 1 16.544-4.971q4.007-.944 8.078-1.654c1.291-.226 2.582-.428 3.882-.621.307-.04.613-.089.92-.129-1.114.145 1.146-.161.024 0h-.023l1.945-.242a142.385 142.385 0 0 1 14.841-.928c4.592-.048 9.184.121 13.751.5 2.122.186 4.245.4 6.359.678l.371.048-.347-.048c-.678-.1.662.089.718.1l-.371-.048.1.016c.492.065.976.137 1.469.21.993.153 1.985.307 2.97.476q5.435.92 10.782 2.252c3.042.759 6.069 1.606 9.063 2.566q3.559 1.138 7.061 2.469c.912.355 1.824.718 2.728 1.1l.056.016c-.161-.065-.315-.121-.468-.194.008 0 1.412.589.71.3l-.242-.1.1.048c.339.145.678.291 1.017.444.847.371 1.679.767 2.518 1.17.1.056.2.1.307.161a4.4303595 4.4303595 0 1 0 4.471-7.65" fill="#666"/><path data-name="Path 502" d="M223.015 71.972h.024c1.122-.161-1.146.145-.024 0" fill="#666"/><path data-name="Path 503" d="M294.063 80.938c.153.073.307.129.46.194a2.326 2.326 0 0 0 .25.1c.694.291-.7-.3-.71-.3" fill="#666"/><path data-name="Path 504" d="m259.943 71.98.347.048.371.048c-.056-.008-1.4-.194-.718-.1" fill="#666"/><path data-name="Path 505" d="M304.966 77.953c-.371-1.081-.8-2.147-1.235-3.2-1.3-3.163-2.7-6.287-4.221-9.353q-3.026-6.1-6.617-11.911a146.143 146.143 0 0 0-10.152-14.385 118.3 118.3 0 0 0-12.783-13.735 104.692 104.692 0 0 0-16.818-12.573A89.918 89.918 0 0 0 208.134.021a111.465 111.465 0 0 0-28.2 3.3 159.048 159.048 0 0 0-32.32 12.016c-10.093 4.939-20.027 10.267-29.759 15.874-8.014 4.616-15.89 9.49-23.427 14.849a161.958 161.958 0 0 0-18.325 14.938 100.15 100.15 0 0 0-13.237 15.494 80.231 80.231 0 0 0-8.611 16.552 85.494 85.494 0 0 0-4.56 17.383 140.328 140.328 0 0 0-1.63 20.732c-.065 7.562.323 15.131.9 22.677.678 8.869 1.622 17.714 2.631 26.55 1.178 10.289 2.453 20.57 3.68 30.852q1.017 8.425 1.977 16.85c.258 2.3 1.848 4.551 4.43 4.43 2.171-.1 4.713-1.961 4.43-4.43q-.375-3.3-.767-6.617c4.366-10.6 23.661-55.82 44.974-79.933 24.258-27.446 43.586-49.259 68.119-58.677 7.182-2.76 14.97-6.343 24-9.515 32.191 3.4 25.582 42.3 25.582 42.3 24.315-17.633 11.363-40.221 4.64-49.154 16.4-1.6 36.251-.137 61.171 7.537.1.226.2.444.307.662.282.638.565 1.275.839 1.913.089.226.186.452.274.678.307.751.605 1.5.888 2.252.1.258.194.516.282.775.751 2.187 3.067 3.866 5.447 3.1 2.163-.7 3.906-3.107 3.091-5.455m-9.857-1.058.065.145-.065-.145" fill="#666"/><path data-name="Path 506" d="M32.352 149.436 46.62 272.391l-16.7 53.553S7.198 285.851 5.81 263.837s7.862-97.632 26.542-114.401" fill="#666"/><path data-name="Path 507" d="M466.988 353.713a23.73 23.73 0 0 1-23.93-24.041v-129.87c0-13.719 11.048-25.953 24.63-27.261a36.29 36.29 0 0 1 3.664-.186c10.386 0 18.343 4.7 23.016 13.59l27.4 51.809 27.971-58.241c4.785-10.023 13.751-16.156 25.259-17.262.815-.073 1.63-.113 2.429-.113a23.648 23.648 0 0 1 23.741 24.058v129.87a27.678 27.678 0 0 1-24.872 27.282 24.751 24.751 0 0 1-19.675-6.86 23.854 23.854 0 0 1-6.972-17.028v-37.63l-2.163 4.7a30.167 30.167 0 0 1-24.6 17.778c-.92.089-1.816.137-2.7.137a25.948 25.948 0 0 1-23.322-14.187l-2.3-4.551v40.576c0 13.735-11.266 25.977-25.106 27.3-.831.081-1.654.121-2.477.121" fill="#f7f7ea"/><g data-name="Group 403" opacity=".8"><g data-name="Group 402"><g data-name="Group 401" clip-path="url(#c)" transform="translate(434.987 154.069)"><path data-name="Path 508" d="M142.43 16.139a15.594 15.594 0 0 1 15.68 15.987v129.87a19.711 19.711 0 0 1-17.568 19.255c-.589.056-1.17.089-1.743.089a15.763 15.763 0 0 1-16.059-15.946V93.926l-1.428.137-16.14 35.024a21.986 21.986 0 0 1-18.045 13.13c-.654.056-1.3.089-1.937.089a17.756 17.756 0 0 1-16.108-9.765l-16.14-31.925-1.428.137v71.468c0 9.49-7.828 18.319-17.8 19.271-.573.056-1.146.089-1.711.089a15.6 15.6 0 0 1-15.861-15.973V45.732c0-9.5 7.594-18.295 17.334-19.231 1-.1 1.961-.145 2.889-.145 7.626 0 12.686 3.228 15.858 9.272l34.669 65.529.468-.04 34.661-72.179c3.567-7.465 9.975-11.879 18.755-12.718a16.446 16.446 0 0 1 1.654-.081m0-16.14c-1.049 0-2.114.048-3.2.153-14.437 1.388-25.719 9.143-31.78 21.837l-21.022 43.78-19.937-37.686c-6.028-11.5-16.737-17.867-30.125-17.867q-2.118 0-4.43.218A35.686 35.686 0 0 0 0 45.732v129.876a31.716 31.716 0 0 0 32 32.111c1.065 0 2.155-.056 3.252-.161 18.174-1.735 32.4-17.262 32.4-35.339v-18.527a34.364 34.364 0 0 0 17.536 4.753c1.146 0 2.308-.056 3.478-.169a37.683 37.683 0 0 0 17.934-6.528v13.646a31.8 31.8 0 0 0 32.2 32.086c1.081 0 2.179-.056 3.284-.161a35.8 35.8 0 0 0 32.167-35.322V32.126c0-18.012-13.977-32.127-31.82-32.127" fill="#666"/></g></g></g><path data-name="Path 509" d="M630.833 338.008a23.747 23.747 0 0 1-23.968-24.032 23.2 23.2 0 0 1 1.033-7.3l31.78-117.12c6.666-24.129 18.843-35.306 40.721-37.4 2.365-.234 4.584-.347 6.674-.347 23.411 0 31.118 15.753 35.565 31.158l31.578 111.2a21.7 21.7 0 0 1 1.017 7.134c0 13.727-11.153 25.969-24.864 27.277a24.487 24.487 0 0 1-2.542.129c-10.991 0-20.183-6.859-22.879-17.06l-4.955-19.32-37.509 3.591-5.278 21.652a27.612 27.612 0 0 1-23.928 20.312c-.831.081-1.638.121-2.445.121m43.094-88.125 14.873-1.428-7.435-28.7Z" fill="#f7f7ea"/><g data-name="Group 406" opacity=".8"><g data-name="Group 405"><g data-name="Group 404" clip-path="url(#d)" transform="translate(598.8 143.741)"><path data-name="Path 510" d="M88.281 16.138c15.155 0 22.515 7 27.809 25.324l31.562 111.18a13.925 13.925 0 0 1 .718 4.915 19.9 19.9 0 0 1-17.56 19.255c-.6.056-1.194.089-1.775.089-7.3 0-13.332-4.406-15.083-11.056l-6.658-25.958-50.091 4.8-6.642 27.228a19.386 19.386 0 0 1-16.85 14.2c-.565.056-1.122.081-1.671.081a15.745 15.745 0 0 1-15.9-15.962 15.286 15.286 0 0 1 .7-5.052L48.657 47.926c5.94-21.458 15.906-29.778 33.725-31.481q3.111-.3 5.9-.307m-23.718 99.116 35.621-3.406-16.633-64.18-2.365.234-16.624 67.352M88.281-.002c-2.365 0-4.786.121-7.449.379-25.057 2.4-40.237 16.14-47.726 43.247L1.27 160.954a30.514 30.514 0 0 0-1.267 9.281 31.728 31.728 0 0 0 32.038 32.1c1.049 0 2.13-.048 3.2-.153a35.351 35.351 0 0 0 30.973-26.324l3.954-16.2 24.912-2.389 3.22 12.573c3.7 13.921 16.027 23.193 30.731 23.193 1.1 0 2.2-.056 3.309-.161a35.817 35.817 0 0 0 32.167-35.322 29.8 29.8 0 0 0-1.428-9.684l-31.47-110.813C127.001 21.109 117.535-.002 88.281-.002" fill="#666"/></g></g></g><path data-name="Path 511" d="M784.874 321.586a23.728 23.728 0 0 1-23.928-24.033V170.999c0-13.735 11.161-25.977 24.872-27.285l30.384-2.913c4.3-.42 8.49-.621 12.444-.621 32.038 0 70.225 14.316 70.225 82.54 0 76.617-51.124 92.958-81.6 95.88l-29.916 2.865c-.839.081-1.662.121-2.486.121m27.591-50.082 3.26-.307c17.439-1.671 31.877-6.222 31.877-45.111 0-38.228-13.461-38.228-23.3-38.228-2.106 0-4.285.129-6.561.347l-5.278.508Z" fill="#f7f7ea"/><g data-name="Group 409" opacity=".8"><g data-name="Group 408"><g data-name="Group 407" clip-path="url(#e)" transform="translate(752.879 132.108)"><path data-name="Path 512" d="M75.766 16.134c36.105 0 62.163 18.383 62.163 74.47 0 62.212-35.613 84.146-74.309 87.85l-29.916 2.865c-.573.056-1.146.089-1.711.089a15.6 15.6 0 0 1-15.85-15.971V38.883a19.888 19.888 0 0 1 17.56-19.247l30.392-2.921c3.987-.379 7.884-.581 11.669-.581M51.511 148.272l12.105-1.162c23.032-2.2 39.172-11.823 39.172-53.133 0-37.187-12.412-46.3-31.368-46.3-2.348 0-4.794.137-7.328.379L51.511 49.27v99M75.774-.006c-4.221 0-8.659.226-13.219.662L32.171 3.569A35.815 35.815 0 0 0 .005 38.883v126.554a31.709 31.709 0 0 0 31.99 32.111c1.073 0 2.163-.056 3.252-.161l29.916-2.865c56.5-5.415 88.907-43.288 88.907-103.918 0-33.838-10.176-90.61-78.3-90.61m-8.118 63.963c1.348-.089 2.6-.137 3.769-.137 7.828 0 15.228 0 15.228 30.158 0 29.706-8.336 34.79-19 36.428Z" fill="#666"/></g></g></g><path data-name="Path 513" d="M933.378 307.343c-13.574 0-23.807-10.338-23.807-24.041V156.748a27.344 27.344 0 0 1 24.864-27.285l72.646-6.956c.759-.073 1.493-.113 2.235-.113a22.055 22.055 0 0 1 22.273 22.306c0 12.686-10.3 23.992-22.967 25.211l-47.54 4.56v15.6l31.755-3.043a22.261 22.261 0 0 1 24.5 21.959c0 12.678-10.289 23.984-22.943 25.211l-33.313 3.188v19.876l46-4.406c.759-.073 1.493-.113 2.235-.113a22.062 22.062 0 0 1 22.273 22.314c0 12.67-10.3 23.984-22.959 25.2l-72.654 6.956a25.939 25.939 0 0 1-2.6.129" fill="#f7f7ea"/><g data-name="Group 412" opacity=".8"><g data-name="Group 411"><g data-name="Group 410" clip-path="url(#f)" transform="translate(901.493 114.326)"><path data-name="Path 514" d="M107.815 16.138a14.073 14.073 0 0 1 14.2 14.236 17.8 17.8 0 0 1-15.672 17.173l-54.832 5.261V84.62l40.6-3.89c.508-.048 1.009-.073 1.509-.073a13.861 13.861 0 0 1 14.155 14 17.794 17.794 0 0 1-15.664 17.175l-40.6 3.89v36.089l54.836-5.254c.492-.048.985-.073 1.469-.073a14.08 14.08 0 0 1 14.2 14.244 17.8 17.8 0 0 1-15.672 17.173l-72.638 6.956a17.608 17.608 0 0 1-1.832.089c-9.055 0-15.737-6.625-15.737-15.971V42.422a19.372 19.372 0 0 1 17.568-19.255l72.638-6.956c.492-.048.985-.073 1.469-.073m0-16.14c-.993 0-2 .048-3.01.145L32.164 7.099A35.392 35.392 0 0 0 0 42.422v126.554c0 18.311 13.7 32.111 31.877 32.111 1.106 0 2.235-.048 3.373-.161l72.638-6.956c16.7-1.606 30.271-16.519 30.271-33.24a30.4 30.4 0 0 0-30.343-30.384c-.993 0-1.993.048-3.01.145l-37.154 3.559v-3.664l26-2.486c16.689-1.606 30.263-16.519 30.263-33.24a29.827 29.827 0 0 0-27.083-29.98l11.059-1.066c16.7-1.606 30.271-16.519 30.271-33.24A30.392 30.392 0 0 0 107.819-.002" fill="#666"/></g></g></g><path data-name="Path 515" d="M1085.651 294.583c-6.593 0-16.213-.412-24.589-2.4-16.132-3.93-24.194-13.186-25.4-29.133l-.718-10.443c-1.283-19.9 11.185-28.18 22.249-29.238a25 25 0 0 1 2.574-.129c12.42 0 21.781 8.635 23.29 21.482l.258 2.389c1.461.081 3.051.129 4.745.129 3.349 0 6.545-.169 8.756-.379 4.592-.444 15.26-2.276 15.26-9.8 0-5.254-6.956-7.876-24.049-13.025l-1.541-.468c-21.66-6.537-51.325-15.5-51.325-51.075 0-33.442 25.509-57.5 64.988-61.284 3.333-.323 6.763-.492 10.225-.492a84.238 84.238 0 0 1 22.62 2.865c15.18 4.438 22.895 13.243 24.388 27.737l.939 9.678c1.1 10.435-2.3 17.068-5.334 20.78a24.829 24.829 0 0 1-17.108 8.72 25.454 25.454 0 0 1-2.574.129c-12.121 0-21.168-8.352-23.04-21.289l-.177-1.065c-1.114-.04-2.219-.065-3.325-.065-2.13 0-4.229.081-6.254.282-13.364 1.283-13.364 5.964-13.364 7.957 0 3.252 2.913 5.052 22.217 10.693 20.393 6.093 54.473 16.261 54.473 53.415 0 33.684-26.922 59.783-65.472 63.471-3.817.371-8.215.557-12.71.557" fill="#f7f7ea"/><g data-name="Group 415" opacity=".8"><g data-name="Group 414"><g data-name="Group 413" clip-path="url(#g)" transform="translate(1026.772 102.647)"><path data-name="Path 516" d="M83.581 16.14a76.859 76.859 0 0 1 20.474 2.574c11.629 3.4 17.334 9.256 18.513 20.78l.952 9.644c1.428 13.63-6.416 19.844-15.2 20.683a16.855 16.855 0 0 1-1.792.089c-7.884 0-13.735-5.221-15.059-14.373l-1.194-7.489a101.215 101.215 0 0 0-10.281-.557c-2.4 0-4.769.1-7.045.323-15.2 1.453-20.651 7.675-20.651 15.987 0 9.732 7.836 12.541 28.011 18.44 24.686 7.376 48.67 16.471 48.67 45.676 0 29.439-23.984 52.156-58.169 55.433-3.341.315-7.5.525-11.944.525a104.166 104.166 0 0 1-22.717-2.187c-12.573-3.067-18.279-9.41-19.231-21.9l-.71-10.378c-.944-14.631 7.368-19.933 14.954-20.659a17.159 17.159 0 0 1 1.808-.089c7.755 0 14.179 4.971 15.293 14.357l.944 8.691a63.331 63.331 0 0 0 12.089.952c3.64 0 7.11-.186 9.515-.412 14.72-1.412 22.548-8.336 22.548-17.835 0-12.113-12.807-15.624-31.328-21.208-23.266-7.037-45.579-14.865-45.579-43.36 0-26.825 19.933-49.631 57.684-53.246 3.075-.3 6.246-.46 9.442-.46m0-16.14c-3.615 0-7.311.178-10.983.533C29.356 4.674.312 32.534.312 69.847c0 22.055 10.055 35.452 22.685 44.119-13.162 3.93-24.226 15.89-22.895 36.485l.718 10.443c1.485 19.505 12.081 31.739 31.505 36.476 9.224 2.187 19.513 2.647 26.542 2.647 4.745 0 9.418-.21 13.485-.605 42.844-4.1 72.767-33.5 72.767-71.492 0-22.281-10.434-36.178-23.419-45.273a31.237 31.237 0 0 0 10.763-8.411c4-4.907 8.5-13.533 7.11-26.776l-.944-9.547c-1.816-17.754-11.927-29.4-30.053-34.693A92.639 92.639 0 0 0 83.583 0M75.19 63.818c.516-.04 1.049-.081 1.6-.1l.218.686q-.92-.291-1.816-.581m-11.758 72.679a34.069 34.069 0 0 0-2.485-6.763 127.458 127.458 0 0 1 14.663 5.084 23.8 23.8 0 0 1-6.335 1.364c-1.646.153-3.728.266-5.843.315" fill="#666"/></g></g></g><path data-name="Path 517" d="M1233.469 280.679c-37.114 0-61.09-25.162-61.09-64.108v-86.672a27.674 27.674 0 0 1 24.864-27.277c.831-.081 1.662-.121 2.478-.121a23.728 23.728 0 0 1 23.927 24.032v87.14c0 8.482 2.5 18.593 14.421 18.593q1.15 0 2.373-.121c17.028-1.63 18.351-18.311 18.351-23.387v-87.139a27.682 27.682 0 0 1 24.864-27.285c.831-.081 1.662-.113 2.477-.113a23.723 23.723 0 0 1 23.928 24.032v86.656c0 34.265-21.047 70.855-68.071 75.35-2.945.282-5.778.42-8.522.42" fill="#f7f7ea"/><g data-name="Group 418" opacity=".8"><g data-name="Group 417"><g data-name="Group 416" clip-path="url(#h)" transform="translate(1164.309 86.143)"><path data-name="Path 518" d="M121.825 16.141a15.6 15.6 0 0 1 15.858 15.971v86.655c0 32.764-19.941 63.406-60.775 67.32-2.671.258-5.262.379-7.747.379-35.613 0-53.028-25.429-53.028-56.046V43.757a19.723 19.723 0 0 1 17.568-19.255c.581-.048 1.154-.081 1.711-.081A15.6 15.6 0 0 1 51.27 40.392v87.135c0 14.284 6.714 26.655 22.5 26.655 1.009 0 2.05-.048 3.139-.153 17.8-1.7 25.638-16.221 25.638-31.417V35.477a19.723 19.723 0 0 1 17.57-19.255c.573-.056 1.146-.081 1.711-.081m0-16.14c-1.065 0-2.155.048-3.252.153a35.822 35.822 0 0 0-32.17 35.323v87.14c0 6.521-1.913 14.478-11.04 15.349a15.808 15.808 0 0 1-1.6.081c-2.219 0-6.359 0-6.359-10.515V40.391a31.711 31.711 0 0 0-32-32.111c-1.065 0-2.155.048-3.252.153A35.823 35.823 0 0 0-.008 43.757v86.664c0 43.175 27.8 72.186 69.168 72.186 3.018 0 6.141-.153 9.289-.452 52.068-4.987 75.374-45.475 75.374-83.387V32.112a31.716 31.716 0 0 0-32-32.111" fill="#666"/></g></g></g><path data-name="Path 519" d="M1349.468 269.131a23.74 23.74 0 0 1-23.968-24.024V116.89c0-13.735 11.161-25.977 24.872-27.285l35.6-3.422c2.97-.282 5.9-.42 8.748-.42 37.687 0 63 23.879 63 59.411 0 35.928-30.836 67.03-70.2 70.806l-10.515 1.009v24.718c0 13.735-11.266 25.977-25.106 27.3-.823.073-1.63.113-2.445.113m27.543-99.463 8.49-.815c12.969-1.235 20.716-11.29 20.716-20.288 0-6.48-1.622-15.1-15.7-15.1-1.122 0-2.284.056-3.478.169l-10.031.96Z" fill="#f7f7ea"/><g data-name="Group 421" opacity=".8"><g data-name="Group 420"><g data-name="Group 419" clip-path="url(#i)" transform="translate(1317.43 77.69)"><path data-name="Path 520" d="M77.303 16.144c31.449 0 54.924 18.674 54.924 51.341 0 31.336-27.527 59.379-62.906 62.769l-17.8 1.711v32.054c0 9.5-7.836 18.319-17.8 19.271-.565.056-1.122.081-1.671.081a15.74 15.74 0 0 1-15.9-15.962V39.192a19.9 19.9 0 0 1 17.566-19.255l35.6-3.406c2.712-.266 5.375-.387 7.981-.387m-25.784 84.711 17.326-1.654c16.858-1.614 28.019-14.792 28.019-28.334 0-15.39-9.313-23.161-23.766-23.161-1.372 0-2.784.065-4.253.21l-17.32 1.661v51.277M77.303.004c-3.115 0-6.319.153-9.523.46l-35.6 3.414A35.809 35.809 0 0 0 .013 39.192v128.216a31.728 31.728 0 0 0 32.038 32.1c1.057 0 2.13-.048 3.212-.153 18.166-1.743 32.4-17.262 32.4-35.339v-17.388l3.2-.307c43.465-4.164 77.5-38.793 77.5-78.836 0-39.737-29.221-67.481-71.064-67.481m-9.642 64.237 2.728-.258c.936-.089 1.832-.137 2.712-.137 6.964 0 7.626 1.921 7.626 7.021 0 5.326-4.882 11.29-13.065 12.226Z" fill="#666"/></g></g></g><path data-name="Path 521" d="M1486.985 254.274c-13.566 0-23.79-10.33-23.79-24.041V103.687a27.334 27.334 0 0 1 24.872-27.285l72.641-6.967c.759-.073 1.485-.113 2.219-.113a22.066 22.066 0 0 1 22.29 22.313c0 12.678-10.3 23.984-22.967 25.2l-47.541 4.56v15.6l31.748-3.042a22.269 22.269 0 0 1 24.508 21.963c0 12.686-10.3 23.992-22.959 25.211l-33.3 3.188v19.876l46-4.406c.759-.073 1.493-.113 2.227-.113a22.064 22.064 0 0 1 22.281 22.314c0 12.678-10.3 23.992-22.967 25.2l-72.646 6.956a26.131 26.131 0 0 1-2.615.129" fill="#f7f7ea"/><g data-name="Group 424" opacity=".8"><g data-name="Group 423"><g data-name="Group 422" clip-path="url(#j)" transform="translate(1455.128 61.259)"><path data-name="Path 522" d="M107.807 16.137a14.082 14.082 0 0 1 14.211 14.244 17.8 17.8 0 0 1-15.671 17.173l-54.836 5.253v31.812l40.6-3.89q.763-.073 1.5-.073a13.868 13.868 0 0 1 14.163 14 17.789 17.789 0 0 1-15.664 17.173l-40.6 3.89v36.088l54.836-5.254c.492-.048.977-.073 1.461-.073a14.087 14.087 0 0 1 14.211 14.244 17.8 17.8 0 0 1-15.672 17.173l-72.638 6.956a17.622 17.622 0 0 1-1.832.089c-9.054 0-15.736-6.625-15.736-15.971V42.421a19.359 19.359 0 0 1 17.568-19.247l72.639-6.967c.492-.048.977-.073 1.461-.073m0-16.14c-.984 0-1.985.048-2.994.145L32.167 7.107A35.372 35.372 0 0 0 0 42.421v126.554c0 18.311 13.7 32.111 31.876 32.111 1.106 0 2.227-.048 3.373-.161l72.638-6.956c16.689-1.6 30.271-16.511 30.271-33.24a30.4 30.4 0 0 0-30.351-30.384c-.984 0-1.985.048-2.994.145l-37.162 3.559v-3.656l26-2.494c16.681-1.6 30.263-16.511 30.263-33.24a29.838 29.838 0 0 0-27.083-29.98l11.056-1.058c16.689-1.6 30.271-16.511 30.271-33.24A30.4 30.4 0 0 0 107.807-.003" fill="#666"/></g></g></g><path data-name="Path 523" d="M1617.497 243.444a23.747 23.747 0 0 1-23.968-24.033V91.195c0-13.735 11.161-25.969 24.872-27.285l41.544-3.987c2.429-.234 4.834-.347 7.2-.347 34.241 0 59.1 23.871 59.1 56.773 0 18.149-8.288 34.5-23.008 45.943l21.143 31.255c1.21 1.792 5.189 8.167 5.189 14.849 0 13.921-10.8 25.461-25.122 26.825a20.558 20.558 0 0 1-2.106.1c-9.474 0-16.826-5.907-20.546-11.427l-27.591-43.215-9.151.88v34.451c0 13.735-11.266 25.985-25.114 27.309-.823.081-1.63.121-2.437.121m27.551-105.4 12.767-1.21c10.58-1.017 17.149-7.578 17.149-17.117 0-7.812-4.818-12.3-13.211-12.3-.775 0-1.582.04-2.4.121l-14.3 1.372Z" fill="#f7f7ea"/><g data-name="Group 427" opacity=".8"><g data-name="Group 426"><g data-name="Group 425" clip-path="url(#k)" transform="translate(1585.459 51.506)"><path data-name="Path 524" d="M81.685 16.141c28.761 0 51.027 19.061 51.027 48.7 0 19.707-10.919 34.766-26.123 43.812l25.648 37.916c1.662 2.453 3.8 6.755 3.8 10.313 0 10.217-8.078 17.875-17.811 18.8a12.661 12.661 0 0 1-1.348.065c-6.069 0-11.193-3.914-13.848-7.868l-30.15-47.2-21.361 2.05v41.79c0 9.5-7.836 18.319-17.811 19.271a16.489 16.489 0 0 1-1.662.081 15.747 15.747 0 0 1-15.909-15.965V39.689a19.9 19.9 0 0 1 17.568-19.255l41.544-3.979q3.256-.315 6.432-.315M51.519 95.419l21.6-2.074c14.712-1.4 24.452-11.363 24.452-25.13 0-12.323-8.352-20.369-21.281-20.369-1.033 0-2.09.048-3.172.153l-21.6 2.074v45.346M81.684-.001c-2.623 0-5.31.129-7.973.387L32.175 4.373A35.809 35.809 0 0 0 0 39.689v128.217a31.73 31.73 0 0 0 32.046 32.1c1.049 0 2.13-.048 3.2-.153 18.174-1.743 32.409-17.262 32.409-35.339v-22.021l21.765 34.08c5.092 7.57 14.808 15.317 27.454 15.317.952 0 1.913-.04 2.889-.137 18.48-1.767 32.409-16.761 32.409-34.871 0-8.708-4.6-16.439-6.569-19.36l-17.125-25.316a64.752 64.752 0 0 0 20.369-47.363C148.847 27.277 120.594 0 81.68 0M67.659 64.739l7-.67a16.089 16.089 0 0 1 1.63-.081c5.141 0 5.141 1.961 5.141 4.229 0 6.714-5.358 8.635-9.854 9.063l-3.922.379Z" fill="#666"/></g></g></g><path data-name="Path 525" d="M1058.006 358.062a24.555 24.555 0 0 1-2.381-48.993l644.863-63.438c.823-.073 1.622-.113 2.429-.113a24.556 24.556 0 0 1 2.372 48.993l-644.867 63.43a24.079 24.079 0 0 1-2.421.121" fill="#f7f7ea"/><g data-name="Group 430" opacity=".8"><g data-name="Group 429"><g data-name="Group 428" clip-path="url(#l)" transform="translate(1025.392 237.449)"><path data-name="Path 526" d="M677.527 16.139a16.486 16.486 0 0 1 1.582 32.893l-644.87 63.431a15.971 15.971 0 0 1-1.63.081 16.486 16.486 0 0 1-1.6-32.893l644.867-63.43a16.287 16.287 0 0 1 1.646-.081m0-16.14a30.684 30.684 0 0 0-3.212.161L29.432 63.591a32.625 32.625 0 0 0 3.18 65.093c1.049 0 2.122-.048 3.188-.153l644.89-63.439a32.624 32.624 0 0 0-3.163-65.093" fill="#666"/></g></g></g><path data-name="Path 527" d="M458.247 138.825a18.855 18.855 0 0 1-18.827-18.836V42.444a18.855 18.855 0 0 1 18.827-18.835h28.955c22.467 0 40.076 15.559 40.076 35.419a29.767 29.767 0 0 1-7.118 19.763 33.05 33.05 0 0 1 9.886 23.734c0 20.692-17.359 36.3-40.366 36.3Zm29.972-34.476c3.551 0 4-1.751 4-3.276 0-2.373-1.348-3.567-4-3.567h-10.983v6.843Zm-1.315-40.294a2.954 2.954 0 0 0 2.7-2.986c0-1.275-1.057-3.131-2.7-3.131h-9.668v6.117Z" fill="#f7f7ea"/><g data-name="Group 433" opacity=".8"><g data-name="Group 432"><g data-name="Group 431" clip-path="url(#m)" transform="translate(431.355 15.54)"><path data-name="Path 528" d="M55.851 16.139c17.02 0 32.006 11.056 32.006 27.341 0 8.74-4.366 15.72-12.073 20.078 10.475 5.681 14.841 14.55 14.841 23.427 0 15.995-13.679 28.221-32.3 28.221H26.9a10.73 10.73 0 0 1-10.757-10.765V26.904A10.823 10.823 0 0 1 26.9 16.139Zm-18.04 40.447h17.746a10.994 10.994 0 0 0 10.765-11.064c0-5.665-4.511-11.2-10.765-11.2H37.811v22.264m0 40.294h19.053c7.723 0 12.073-5.092 12.073-11.355 0-6.246-4.35-11.637-12.073-11.637H37.811V96.88M55.851-.001H26.9A26.933 26.933 0 0 0 0 26.904v77.537a26.933 26.933 0 0 0 26.9 26.905h31.425c27.616 0 48.436-19.069 48.436-44.361a40.866 40.866 0 0 0-7.779-24.267 38.414 38.414 0 0 0 5.011-19.239c0-24.38-21.151-43.481-48.146-43.481" fill="#666"/></g></g></g><path data-name="Path 529" d="M581.573 139.833a18.856 18.856 0 0 1-18.835-18.835V96.626l-24.113-46.547a20.184 20.184 0 0 1-2.365-9.093c0-10.313 8.465-18.4 19.271-18.4a19.662 19.662 0 0 1 17.7 11.9l8.78 19.554 8.798-19.569c3.378-7.553 9.511-11.885 16.806-11.885 10.806 0 19.271 8.086 19.271 18.4a20.335 20.335 0 0 1-2.4 9.176l-24.077 46.464v24.371a18.856 18.856 0 0 1-18.835 18.835" fill="#f7f7ea"/><g data-name="Group 436" opacity=".8"><g data-name="Group 435"><g data-name="Group 434" clip-path="url(#n)" transform="translate(528.191 14.519)"><path data-name="Path 530" d="M79.424 16.143c5.964 0 11.2 4.221 11.2 10.33a12.349 12.349 0 0 1-1.453 5.383L64.148 80.154v26.332a10.7655 10.7655 0 0 1-21.531 0V80.154l-25.025-48.3a12.349 12.349 0 0 1-1.453-5.383c0-6.109 5.237-10.33 11.2-10.33a11.518 11.518 0 0 1 10.33 7.126l16.149 35.937 16.156-35.936c1.888-4.213 4.939-7.126 9.45-7.126m0-16.14c-10.459 0-19.505 6.23-24.178 16.657l-1.428 3.172-1.428-3.18A27.714 27.714 0 0 0 27.34.004C12.007.004-.001 11.633-.001 26.474a28.463 28.463 0 0 0 3.357 12.985l23.121 44.625v22.4a26.9055 26.9055 0 1 0 53.811 0v-22.4l23.209-44.813a28.1 28.1 0 0 0 3.268-12.8c0-14.841-12.016-26.47-27.341-26.47" fill="#666"/></g></g></g></g></g></g></svg>';

      const mapboxBottomLeft = document.getElementsByClassName(
        "mapboxgl-ctrl-bottom-left"
      )[0];

      if (mapboxBottomLeft) {
        mapboxBottomLeft.insertAdjacentHTML(
          "beforeend",
          '<div class="mapboxgl-ctrl"><a href="https://sosuper.app" target="_blank" title="powered by sosuper.app">' +
            svg +
            "</a></div>"
        );
      }
    }

    trackEvent("Map load");

    if (this.geolocateOnLoadValue) {
      this.geolocate();
    } else {
      this.query();
    }
  }

  handleMapMove() {
    if (this.searchAsIMoveTheMapValue) this.searchCurrentMapView();
  }

  searchCurrentMapView() {
    if (!this.map) return;
    this.searchCoords = undefined;
    this.search({ bbox: this.map.getBounds() });
  }
  toggleSearchAsIMoveTheMap() {
    this.searchAsIMoveTheMapValue = !this.searchAsIMoveTheMapValue;
  }
  searchAsIMoveTheMapValueChanged(newValue) {
    if (newValue) {
      this.searchCurrentMapView();
      this.element.classList.add(this.searchAsIMoveTheMapClass);
    } else {
      this.element.classList.remove(this.searchAsIMoveTheMapClass);
    }
  }

  searchButtonTargetConnected(searchButton) {
    this.setListingsLoading();
    searchButton.addEventListener("click", () => this.query());
  }

  search(options = {}) {
    if (this.hasSearchButtonTarget) return;
    this.setListingsLoading();
    // Map-only page variants have no listings panel; the map still loads but
    // there is nowhere to render results, so skip the query entirely.
    if (!this.hasListingsContainerTarget) return;
    this.query(options);
    // console.log("SEARCHING");
    // this.debounce(() => this.query(bbox))();
  }

  query(options = {}){
    if (!this.hasListingsContainerTarget) return;
    if (window.locationsMadeSuperConfig.beta == true){
      this.queryBeta(options);
    }else{
      this.queryOld(options);
    }
  }

  queryBeta(options = {}) {
    //console.log("lr", options.locationResult);
    this.activeLocationIdValue = undefined;
    const proxyPath = this.getLocaleAwareProxyPath();

    if (this.activeQuery) {
      this.abortController.abort();
      this.abortController = new AbortController();
    }

    this.activeQuery = true;
    
    // Get current locale from URL or baseVariables
    const currentLocale = this.getCurrentLocale();
    
    // Don't add ?locale= parameter if we're already using a locale-prefixed path
    const hasLocaleInPath = window.location.pathname.match(/^\/[a-z]{2}(-[a-z]{2})?\//);
    const localeParam = (!hasLocaleInPath && currentLocale) ? `?locale=${currentLocale}` : '';
    
    // console.log(this.searchCoords);
    post(`${proxyPath}/listings/search_v2.json${localeParam}`, {
      body: {
        search: {
          listings_container_id: this.listingsContainerTarget.id,
          coords: this.searchCoords,
          tags: this.activeTagsValue,
          bbox: options.bbox,
          location_result: options.locationResult || false,
          open_now: this.openNowValue,
          max_results: this.maxResultsValue,
          max_distance: this.maxDistanceValue,
          max_distance_units: this.maxDistanceUnits,
          filter_behaviour: this.filterBehaviourValue,
        },
      },
      responseType: "json",
      signal: this.abortController.signal,
    }).then((response) => {
      this.activeQuery = false;
      if (response.ok) {

      }
     return response.json;
    }).then((json) => {

      //delete children of listings container
      while (this.listingsContainerTarget.firstChild) {
        this.listingsContainerTarget.removeChild(this.listingsContainerTarget.firstChild);
      }

      const event = this.dispatch("list_render_liquid", { cancelable: true, detail: {data: json} })

      if (event.defaultPrevented) return
      this.renderLiquid(json);
    });
  }

  renderLiquid(json){
    console.log("render")
     //loop though locations and add them to the template
      //uses sync and not async render may need to use async eg
      // engine.parseAndRender(wrapperTemplate, variables).then((html) => {
    let base = window.baseVariables;
    for (let i = 0; i < json.locations.length; i++) {
      let location = json.locations[i];
      let distance = json.locations[i].distance;

      let variables = {location: location, ...base, distance: distance}
      let html = engine.parseAndRenderSync(wrapperTemplate, variables);
      let template = document.createElement("template");
      template.innerHTML = html;
      let node = template.content.firstElementChild;

      let listingHtml = engine.parseAndRenderSync(this.listingTemplate, variables);
      node.innerHTML = listingHtml;

      //render popup
      if(this.popupTemplate!=null){
        let pTemplate = document.createElement('template');
        pTemplate.classList.add("popup");
        pTemplate.id = "popup-" + location.id;

        let popuphtml = engine.parseAndRenderSync(this.popupTemplate, variables);
        pTemplate.innerHTML = popuphtml;
        node.appendChild(pTemplate);
      }

      //render marker
      if(this.markerTemplate!=null){
        let mTemplate = document.createElement('div');
        mTemplate.innerHTML = engine.parseAndRenderSync(this.markerTemplate, variables);
        mTemplate.classList.add("marker");
        mTemplate.id = "marker-" + location.id;
        node.appendChild(mTemplate);

      }

      this.listingsContainerTarget.appendChild(node);

    }
    //create node from html
    this.createSearchResultTarget(json);

    //update store count
    let listingResultsFound = document.getElementById("listingResultsFound");
    if(listingResultsFound){
      listingResultsFound.innerHTML = json.locations.length;
    }

    let listingResultsTotal = document.getElementById("listingResultsTotal");
    if(listingResultsTotal){
      listingResultsTotal.innerHTML = base.total_locations;
    }
  }

  createSearchResultTarget(json) {
    if (!this.hasListingsContainerTarget) return;
    const searchResultTemp = document.createElement("div");
    searchResultTemp.dataset.mapTarget = "searchResult";
    searchResultTemp.dataset.moveCamera = json.move_camera;
    searchResultTemp.dataset.setFirstActive = json.set_first_active;
    this.listingsContainerTarget.appendChild(searchResultTemp);
  }

  queryOld(options = {}) {
    //console.log("lr", options.locationResult);
    this.activeLocationIdValue = undefined;
    const proxyPath = this.getLocaleAwareProxyPath();

    if (this.activeQuery) {
      this.abortController.abort();
      this.abortController = new AbortController();
    }

    this.activeQuery = true;
    
    // Get current locale from URL or baseVariables
    const currentLocale = this.getCurrentLocale();
    
    // Don't add ?locale= parameter if we're already using a locale-prefixed path
    const hasLocaleInPath = window.location.pathname.match(/^\/[a-z]{2}(-[a-z]{2})?\//);
    const localeParam = (!hasLocaleInPath && currentLocale) ? `?locale=${currentLocale}` : '';
    
    // console.log(this.searchCoords);
    post(`${proxyPath}/listings/search${localeParam}`, {
      body: {
        search: {
          listings_container_id: this.listingsContainerTarget.id,
          listing_template: this.listingTemplate,
          marker_template: this.markerTemplate,
          popup_template: this.popupTemplate,
          coords: this.searchCoords,
          tags: this.activeTagsValue,
          bbox: options.bbox,
          location_result: options.locationResult || false,
          open_now: this.openNowValue,
          max_results: this.maxResultsValue,
          max_distance: this.maxDistanceValue,
          max_distance_units: this.maxDistanceUnits,
          filter_behaviour: this.filterBehaviourValue,
        },
      },
      responseKind: "turbo-stream",
      signal: this.abortController.signal,
    }).then(() => {
      this.activeQuery = false;
    });

  }

  debounce(func, timeout = 2000) {
    return (...args) => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  }

  getCurrentLocale() {
    // First, try to get from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const localeFromUrl = urlParams.get('locale');
    if (localeFromUrl) {
      return localeFromUrl;
    }
    
    // Second, try to get from baseVariables (set by server)
    if (window.baseVariables && window.baseVariables.currentLocale) {
      const currentLocale = window.baseVariables.currentLocale;
      const defaultLocale = window.baseVariables.defaultLocale || 'en';
      // Only include locale if it's different from default
      if (currentLocale !== defaultLocale) {
        return currentLocale;
      }
    }
    
    // Third, try to get from detectedLocale (set by server-side script)
    if (window.detectedLocale) {
      const defaultLocale = window.baseVariables?.defaultLocale || 'en';
      if (window.detectedLocale !== defaultLocale) {
        return window.detectedLocale;
      }
    }
    
    return null;
  }

  getLocaleAwareProxyPath() {
    const baseProxyPath = window.locationsMadeSuperConfig.proxyPath;
    
    // Check if current URL has a locale prefix like /en-it/
    const pathMatch = window.location.pathname.match(/^\/([a-z]{2}(-[a-z]{2})?)\//);
    if (pathMatch) {
      const detectedLocale = pathMatch[1];
      
      // Check if baseProxyPath already starts with this locale to avoid duplication
      if (baseProxyPath.startsWith(`/${detectedLocale}/`)) {
        return baseProxyPath; // Already has locale prefix
      }
      
      // If we're on a locale-prefixed page, use that same prefix for API calls
      return `/${detectedLocale}${baseProxyPath}`;
    }
    
    // No locale prefix detected, use the base proxy path
    return baseProxyPath;
  }

  // this.map.fitBounds(this.listingBounds(), { padding: 100 });

  // Add distance to each location object
  // Re build listings
  // Set first location to be active
  // Zoom to bounding box for search result and closest location
  // Open popup for listing
  // Set closest listing to active

  listingTargetDisconnected(listingElement) {
    const { locationId } = listingElement.dataset;
    if (this.map) {
      const marker = this.element.querySelector(`#marker-${locationId}`);
      marker.remove();
    }
  }

  listingTargetConnected(listingElement) {
    const { lat, lng, locationId } = listingElement.dataset;
    if (locationId == this.activeLocationIdValue) {
      listingElement.classList.add("active");
    }
    if (!this.map) return; // Cant add markers if we dont have a map

    if (document.querySelectorAll(`#marker-${locationId}`).length > 1) {
      return;
    }
    const markerOptions = { anchor: "bottom" };
    const markerElement = listingElement.querySelector(".marker");
    if (this.markerTemplate && !markerElement) {
      return; //Sometimes when your filtering quick it gets messed up and cant find marker element
    }
    const marker = markerElement
      ? new mapboxgl.Marker(markerElement, markerOptions)
      : new mapboxgl.Marker(markerOptions); // Use default marker if we havent specified one

    // Add click event listener to set active location id.
    marker
      .getElement()
      .addEventListener(
        "click",
        () => (this.activeLocationIdValue = locationId)
      );
    marker.setLngLat([lng, lat]).addTo(this.map);
  }

  //
  // Map
  //
  // flyTo(location) {
  //   if (this.map) {
  //     this.map.flyTo({
  //       center: [location.lng, location.lat],
  //       zoom: 15,
  //     });
  //   }
  // }

  // ACTIONS
  toggleTag(event) {
    // event.preventDefault()
    const tagId = event.params.tagId || event.target.selectedOptions[0].value; //For a select
    if (this.activeTagsValue.includes(tagId)) {
      this.activeTagsValue = this.activeTagsValue.filter((id) => id !== tagId);
      event.currentTarget.classList.remove("active");
    } else {
      this.activeTagsValue = this.activeTagsValue.concat([tagId]);
      event.currentTarget.classList.add("active");
    }
  }

  selectTag(event) {
    // Get the selected value from the select
    const selectedTagID = event.currentTarget.value;
    // Get all values from the select
    const tagIDsInSelect = Array.from(event.currentTarget.options).map(
      (option) => option.value
    );

    this.removeTags(tagIDsInSelect);
    // if selectedTagID is an integer then add it to the activeTagsValue
    if (selectedTagID.match(/^\d+$/)) this.addTags([selectedTagID]);
  }

  removeTags(tagIDs) {
    this.activeTagsValue = this.activeTagsValue.filter(
      (id) => !tagIDs.includes(id)
    );
  }

  addTags(tagIDs) {
    this.activeTagsValue = this.activeTagsValue.concat(tagIDs);
  }

  clearTags(event) {
    // Doesnt get rid of active classes
    this.activeTagsValue = [];
    this.search();
  }

  setActiveLocation(event) {
    this.activeLocationIdValue = event.params.id;
  }

  toggleOpenNow() {
    this.openNowValue = !this.openNowValue;
    this.search();
    event.target.classList.toggle("active");
  }

  geolocate(event) {
    if (!this.geolocator) return;

    this.element.classList.add(this.geolocateLoadingClass);
    this.geolocator.trigger();
    this.geolocator.on("geolocate", () => {
      this.element.classList.remove(this.geolocateLoadingClass);
    });
  }

  //
  // Callbacks
  //
  activeLocationIdValueChanged(activeLocationId) {
    // Remove existing popup
    this.clearPopUp();
    // Remove active class on active listing
    this.listingTargets.forEach((lt) => lt.classList.remove("active"));
    // Remove active class on active marker
    this.element
      .querySelectorAll(".marker.active")
      .forEach((lt) => lt.classList.remove("active"));

    const activeListing = this.listingTargets.find(
      (lt) => lt.dataset.locationId == activeLocationId
    );

    this.element.classList.remove("has-active-listing");

    if (!activeListing) return;

    this.element.classList.add("has-active-listing");

    // add active class to listing
    activeListing.classList.add("active");

    // add active class to active marker
    const activeMarker = this.element.querySelector(
      `#marker-${activeLocationId}`
    );
    if (activeMarker) activeMarker.classList.add("active");

    const { lat, lng } = activeListing.dataset;
    // Add popup
    const popup = activeListing.querySelector(".popup");
    if (popup) {
      this.popup = new mapboxgl.Popup({
        closeOnClick: false,
        anchor: "bottom",
        maxWidth: "none",
        focusAfterOpen: false,
      })
        .setLngLat([lng, lat])
        .setHTML(popup.innerHTML)
        .addTo(this.map);
    }

    if (this.searchAsIMoveTheMapValue) return;

    if (this.searchResultTarget.dataset.setFirstActive === "true") return;

    if (this.searchCoords && this.keepSearchInViewValue) {
      // Fly to bbox between search and location
      this.map.fitBounds(this.listingBounds([activeListing]), {
        // if we have a pop up we need to make sure its in the view e.g. 300 + 25
        padding: this.boundsPaddingValue,
      });
    } else {
      //Fly to location
      this.flyTo([lng, lat]);
    }
  }

  listingBounds(listings = this.listingTargets) {
    const lats = listings.map((listing) => listing.dataset.lat);
    const lngs = listings.map((listing) => listing.dataset.lng);
    if (this.searchCoords) {
      lats.push(this.searchCoords[0]);
      lngs.push(this.searchCoords[1]);
    }

    return [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];
  }

  flyTo(coords) {
    if (this.map) {
      this.map.flyTo({
        center: coords,
        // zoom: 15,
        speed: this.animationDurationValue,
      });
    }
  }

  openNowValueChanged(newValue) {
    if (newValue) {
      this.element.classList.add(this.hasOpenNowFilterClass);
    } else {
      this.element.classList.remove(this.hasOpenNowFilterClass);
    }
  }

  activeTagsValueChanged(newTags) {
    if (newTags.length) {
      this.element.classList.add(this.hasActiveTagsClass);
    } else {
      this.element.classList.remove(this.hasActiveTagsClass);
    }
    // this.element.classList.remove(this.noResultsClass);
    if (this.listingTemplate) {
      this.search();
    }
  }
}
